import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Download, ShieldCheck, BookOpen, Lock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { injectJsonLd, removeJsonLd, CANONICAL_BASE } from "@/lib/seo";
import { trackViewItem } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EbookProduct() {
  const { ebookId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Pflegeshop · Dickies-Campus";
    axios.get(`${API}/ebooks/${ebookId}`)
      .then((r) => {
        const p = r.data;
        setProduct(p);
        // SEO: Titel, Description + Product-Rich-Snippet
        document.title = `${p.title} – ${p.subtitle} | Dickies-Campus Pflegeshop`;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = "description";
          document.head.appendChild(meta);
        }
        meta.content = `${String(p.description || "").substring(0, 155)}…`;
        injectJsonLd("ld-ebook", {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": p.title,
          "description": p.description,
          "image": p.cover ? `${CANONICAL_BASE}${p.cover}` : undefined,
          "brand": { "@type": "Brand", "name": "Dickies-Campus" },
          "offers": {
            "@type": "Offer",
            "url": `${CANONICAL_BASE}/shop/${ebookId}`,
            "price": String(p.print_price || p.amount),
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
          },
        });
        trackViewItem(ebookId, p.title, p.amount || 0);
      })
      .catch(() => setError("Produkt nicht gefunden."));
    return () => removeJsonLd("ld-ebook");
  }, [ebookId]);

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/ebooks/checkout`, {
        ebook_id: ebookId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      setError("Checkout konnte nicht gestartet werden – bitte später erneut versuchen.");
      setLoading(false);
    }
  };

  if (error && !product) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="text-terra font-semibold mb-3">Ups.</p>
          <p className="text-sand-700 mb-6">{error}</p>
          <Link to="/" className="inline-flex items-center gap-2 text-sand-800 hover:text-terra font-medium">
            <ArrowLeft className="h-4 w-4" /> Zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <span className="text-sand-500 text-sm">Lade Produkt …</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />
      <main className="pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <Link to="/shop" data-testid="ebook-back-link" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-sand-500 hover:text-terra mb-10">
            <ArrowLeft className="h-3.5 w-3.5" /> Zurück zum Pflegeshop
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* LEFT: Cover */}
            <div className="lg:col-span-5">
              <div className="relative aspect-[3/4] rounded-3xl overflow-hidden shadow-soft border border-cream-300 bg-cream-100">
                {product.cover ? (
                  <img
                    src={product.cover}
                    alt={`Cover: ${product.title}`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-cream-200 to-peach-50 flex flex-col justify-between p-10">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-brand-red mb-4">
                        Praxisbuch · PDF
                      </div>
                      <h2 className="font-heading font-bold text-2xl md:text-3xl text-sand-800 leading-tight tracking-tight">
                        {product.title}
                      </h2>
                      <p className="mt-3 font-heading text-sand-600 text-lg leading-snug">
                        {product.subtitle}
                      </p>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-sand-500 font-semibold">
                      {product.format || "PDF"} · DRM-frei
                    </div>
                  </div>
                )}
              </div>

              {/* Reading sample CTA – below cover */}
              {product.has_preview && (
                <a
                  href={`${API}/ebooks/${ebookId}/preview`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ebook-preview-link"
                  className="mt-5 inline-flex items-center gap-2.5 group text-sand-700 hover:text-terra transition-colors"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white border border-cream-300 group-hover:border-terra group-hover:bg-terra group-hover:text-white transition-all">
                    <FileText className="h-4 w-4" strokeWidth={1.6} />
                  </span>
                  <span>
                    <span className="block font-semibold text-[14px]">Leseprobe ansehen</span>
                    <span className="block text-[11px] text-sand-500 uppercase tracking-[0.18em]">
                      Leseprobe · kostenlos
                    </span>
                  </span>
                </a>
              )}
            </div>

            {/* RIGHT: Product details */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Pflegeshop · Lehrbücher
                </span>
              </div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-[2.9rem] text-sand-800 leading-[1.08] tracking-tight">
                {product.title}
              </h1>
              <p className="mt-5 font-heading text-lg sm:text-xl text-sand-600 leading-snug">
                {product.subtitle}
              </p>

              <p className="mt-7 text-sand-700 text-[15px] sm:text-base leading-[1.7] max-w-xl">
                {product.description}
              </p>

              {/* Bundle contents (nur Digital-Verkauf) */}
              {product.channel !== "amazon" && product.bundle_items?.length > 0 && (
                <div className="mt-8 rounded-2xl bg-cream-100 border border-cream-300 p-6 max-w-xl" data-testid="ebook-bundle-items">
                  <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-terra mb-4">
                    Das ist im Bundle enthalten
                  </div>
                  <ul className="space-y-3">
                    {product.bundle_items.map((item, i) => (
                      <li key={item} className="flex items-start gap-3 text-[15px] text-sand-800">
                        <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white border border-cream-300 text-terra shrink-0 font-heading font-semibold text-[13px]">
                          {i + 1}
                        </span>
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Highlights */}
              <ul className="mt-8 space-y-3 max-w-xl">
                {product.highlights?.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-[15px] text-sand-800">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-terra/15 text-terra shrink-0">
                      <Check className="h-3 w-3" strokeWidth={2.8} />
                    </span>
                    <span className="font-medium">{h}</span>
                  </li>
                ))}
              </ul>

              {/* Price + CTA */}
              {product.channel === "amazon" ? (
                <div className="mt-10 rounded-3xl bg-white border border-cream-300 shadow-soft p-7 md:p-8 max-w-xl" data-testid="ebook-amazon-card">
                  <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-sand-500">
                        Gedruckte Ausgabe
                      </div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="font-heading font-bold text-3xl text-sand-800 tracking-tight" data-testid="ebook-print-price">
                          {product.print_price
                            ? `${product.print_price.toFixed(2).replace(".", ",")} €`
                            : "Bald erhältlich"}
                        </span>
                        {product.print_price && <span className="text-sand-500 text-sm">inkl. USt.</span>}
                      </div>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-brand-red font-semibold">
                      Buchhandel · Amazon
                    </div>
                  </div>

                  {product.amazon_url ? (
                    <a
                      href={product.amazon_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="ebook-amazon-cta"
                      className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-sand-800 hover:bg-sand-900 text-cream-50 h-14 text-base font-semibold transition-colors"
                    >
                      <BookOpen className="h-4 w-4" strokeWidth={1.8} />
                      Bei Amazon kaufen
                    </a>
                  ) : (
                    <div
                      data-testid="ebook-amazon-soon"
                      className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-cream-200 text-sand-600 h-14 text-base font-semibold"
                    >
                      Bald bei Amazon erhältlich
                    </div>
                  )}

                  <p className="mt-4 text-sm text-sand-600" data-testid="ebook-amazon-note">
                    Dieses Lehrbuch erscheint als hochwertige Druckausgabe bei Amazon
                    {product.amazon_url ? "" : " – wir verlinken es hier, sobald es live ist"}.
                    Die Leseprobe kannst du oben kostenlos herunterladen.
                  </p>

                  <div className="mt-5 rounded-2xl bg-cream-100 border border-cream-300 p-5">
                    <p className="text-[13.5px] text-sand-700 leading-relaxed">
                      <span className="font-semibold text-sand-800">Tipp:</span> Im zugehörigen Kurs ist das
                      komplette digitale Lernpaket (Skripte, Übungsfragen, Lernkarten, Checklisten) kostenlos enthalten.
                    </p>
                    <Link
                      to={product.course_url || "/kurse"}
                      data-testid="ebook-course-link"
                      className="mt-3 inline-flex items-center gap-2 text-[13.5px] font-semibold text-terra hover:text-terra-dark transition-colors"
                    >
                      Zur Weiterbildung mit Zertifikat
                      <ArrowLeft className="h-4 w-4 rotate-180" strokeWidth={2} />
                    </Link>
                  </div>
                </div>
              ) : (
              <div className="mt-10 rounded-3xl bg-white border border-cream-300 shadow-soft p-7 md:p-8 max-w-xl">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-sand-500">
                      Einmaliger Kauf
                    </div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="font-heading font-bold text-3xl text-sand-800 tracking-tight">
                        {product.amount.toFixed(2).replace('.', ',')} €
                      </span>
                      <span className="text-sand-500 text-sm">inkl. USt.</span>
                    </div>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-brand-red font-semibold">
                    Sofort-Download
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={loading || product.available === false}
                  data-testid="ebook-checkout-cta"
                  className="mt-6 w-full rounded-full bg-terra hover:bg-terra-dark text-white h-14 text-base font-semibold disabled:opacity-60"
                >
                  {product.available === false ? "Erscheint in Kürze" : loading ? "Weiterleitung …" : (
                    <span className="inline-flex items-center gap-2">
                      <Download className="h-4 w-4" strokeWidth={1.8} />
                      Jetzt für {product.amount.toFixed(0)} € sichern
                    </span>
                  )}
                </Button>

                {product.available === false && (
                  <p className="mt-4 text-sm text-sand-600" data-testid="ebook-unavailable-note">
                    Dieses Produkt erscheint in Kürze. Schreib uns an{" "}
                    <a href="mailto:info@dickies-helden.de" className="underline underline-offset-2 hover:text-terra">
                      info@dickies-helden.de
                    </a>{" "}
                    – wir benachrichtigen dich zum Start.
                  </p>
                )}

                {error && (
                  <p className="mt-4 text-sm text-terra font-medium" data-testid="ebook-checkout-error">{error}</p>
                )}

                <div className="mt-5 grid grid-cols-2 gap-3 text-[12px] text-sand-600">
                  <div className="flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-brand-red" strokeWidth={1.6} />
                    Bezahlung über Stripe
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-3.5 w-3.5 text-brand-red" strokeWidth={1.6} />
                    SSL-verschlüsselt
                  </div>
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-3.5 w-3.5 text-brand-red" strokeWidth={1.6} />
                    DRM-frei lesbar
                  </div>
                  <div className="flex items-center gap-2">
                    <Download className="h-3.5 w-3.5 text-brand-red" strokeWidth={1.6} />
                    Sofort nach Zahlung
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
